import { makeAuthenticatedServiceRequest } from './servicesClient';
import type { FetchError } from 'src/types/fetch';
import type { GenericSelectorProductType } from 'src/types/product';

const ENDPOINT = 'https://api.products.cimpress.io';

export type FetchProductModelParams = {
    productId: string;
    version?: string | number;
};

/**
 * Fetches a product's full definition (options and property rules/assignments) from the Cimpress Product Service.
 *
 * This matches the API endpoint called internally by GenericSelector (`getV2Product`):
 * `GET https://api.products.cimpress.io/v0/products/{productId}/versions/{version}` (or `:current` if no version).
 *
 * Resolves to `null` on 404 or missing product.
 */
export async function fetchProductModel({
    productId,
    version,
}: FetchProductModelParams): Promise<GenericSelectorProductType | null> {
    if (!productId) {
        return null;
    }

    const versionSegment = version !== undefined && version !== '' ? `versions/${version}` : ':current';
    const url = `${ENDPOINT}/v0/products/${encodeURIComponent(productId)}/${versionSegment}`;

    try {
        const response = await makeAuthenticatedServiceRequest<GenericSelectorProductType>({
            url,
            method: 'get',
        });
        return response?.data ?? null;
    } catch (error) {
        if ((error as FetchError)?.response?.status === 404) {
            return null;
        }
        throw error;
    }
}
